import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, Music, Clock, FileAudio, Edit, Layers, BarChart3, Sparkles } from 'lucide-react';
import StemSeparation from './StemSeparation';
import TrackAnalytics from './TrackAnalytics';
import MidiConversionModal from './MidiConversionModal';

export default function SessionDetailsModal({ session, open, onClose }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cleaner');
  
  if (!session) return null;

  const handleEditSession = () => {
    onClose();
    navigate(`/editor/${session.id}`);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownload = (url, filename) => {
    if (!url) return;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasCurses = session.regions && session.regions.length > 0;

  const getBaseTitle = (title) => {
    if (!title) return session.filename || 'Untitled';
    return title
      .replace(/\s*\((Clean|Dirty|Explicit|Radio Edit)\)\s*/gi, ' ')
      .replace(/\s*-\s*(Clean|Dirty|Explicit)\s*/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const baseTitle = getBaseTitle(session.title || session.filename);
  const displayTitle = hasCurses ? `${baseTitle} (Dirty)` : baseTitle;

  const getCleanFilename = (suffix = '') => {
    return `${baseTitle}${suffix}.mp3`;
  };

  const downloadOptions = [
    {
      label: 'Original',
      url: session.files?.original_url,
      filename: getCleanFilename(hasCurses ? ' (Dirty)' : ''),
      available: !!session.files?.original_url,
    },
    {
      label: 'Clean Edit',
      url: session.files?.clean_url,
      filename: getCleanFilename(' (Clean)'),
      available: !!session.files?.clean_url,
    },
    {
      label: 'Clean Acapella',
      url: session.files?.clean_vocal_url,
      filename: getCleanFilename(' (Clean Acapella)'),
      available: !!session.files?.clean_vocal_url,
    },
    {
      label: 'Original Acapella',
      url: session.files?.vocal_url,
      filename: getCleanFilename(hasCurses ? ' (Dirty Acapella)' : ' (Acapella)'),
      available: !!session.files?.vocal_url,
    },
    {
      label: 'Instrumental',
      url: session.files?.instrumental_url,
      filename: getCleanFilename(' (Instrumental)'),
      available: !!session.files?.instrumental_url,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="session-details-modal">
        <DialogHeader>
          <DialogTitle className="sr-only">Session Details</DialogTitle>
          <DialogDescription className="sr-only">
            View and manage your audio session
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Session Info Header */}
          <div className="flex items-start space-x-4">
            {/* Artwork */}
            {session.artwork_url ? (
              <img
                src={session.artwork_url}
                alt={session.title || 'Track artwork'}
                className="h-24 w-24 rounded-lg object-cover flex-shrink-0"
                data-testid="session-modal-artwork"
              />
            ) : (
              <div className="h-24 w-24 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Music className="h-12 w-12 text-primary" />
              </div>
            )}

            {/* Details */}
            <div className="flex-1 min-w-0 space-y-2">
              <div>
                <h3 className="text-xl font-bold break-words" data-testid="session-modal-title">
                  {displayTitle}
                </h3>
                {session.artist && (
                  <p className="text-muted-foreground break-words" data-testid="session-modal-artist">
                    {session.artist}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(session.duration)}</span>
                </div>
                <Badge variant={session.status === 'complete' ? 'default' : 'secondary'}>
                  {session.status}
                </Badge>
                {session.regions && (
                  <Badge variant="outline">
                    {session.regions.length} curse{session.regions.length !== 1 ? 's' : ''} detected
                  </Badge>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Created: {formatDate(session.created_at)}
              </p>
            </div>
          </div>

          {/* Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="cleaner" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Cleaner</span>
              </TabsTrigger>
              <TabsTrigger value="stems" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <Layers className="h-4 w-4" />
                <span className="hidden sm:inline">Stem Separation</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-1.5 text-xs sm:text-sm">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Track Analytics</span>
              </TabsTrigger>
            </TabsList>

            {/* Cleaner Tab */}
            <TabsContent value="cleaner" className="mt-4">
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center space-x-2">
                  <Download className="h-4 w-4" />
                  <span>Download Options</span>
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {downloadOptions.map((option, index) => (
                    <Button
                      key={index}
                      variant={option.available ? 'outline' : 'ghost'}
                      className="justify-start"
                      disabled={!option.available}
                      onClick={() => handleDownload(option.url, option.filename)}
                      data-testid={`download-${option.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <FileAudio className="h-4 w-4 mr-2" />
                      <span className="truncate">{option.label}</span>
                      {!option.available && (
                        <span className="ml-auto text-xs text-muted-foreground">(Not available)</span>
                      )}
                    </Button>
                  ))}
                </div>

                {session.status !== 'complete' && (
                  <p className="text-xs text-muted-foreground text-center">
                    Some files may not be available until processing is complete
                  </p>
                )}

                <Button
                  onClick={handleEditSession}
                  variant="default"
                  className="w-full"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Session
                </Button>
              </div>
            </TabsContent>

            {/* Stem Separation Tab */}
            <TabsContent value="stems" className="mt-4">
              {session.status === 'complete' ? (
                <StemSeparation 
                  sessionId={session.id}
                  existingStems={session.stems}
                  sessionFiles={session.files}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Stem separation is available after processing is complete.</p>
                </div>
              )}
            </TabsContent>

            {/* Track Analytics Tab */}
            <TabsContent value="analytics" className="mt-4">
              {session.status === 'complete' ? (
                <TrackAnalytics 
                  sessionId={session.id} 
                  existingAnalysis={session.track_analysis}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Track analytics is available after processing is complete.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Additional Info - Tags */}
          {session.qualifiers && session.qualifiers.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {session.qualifiers.map((qualifier, index) => (
                  <Badge key={index} variant="secondary">
                    {qualifier}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
